import { useMutation } from "@tanstack/react-query";
import { Button, Card, CardContent, CardDescription, CardHeader, Input } from "@portal/ui";
import { useState } from "react";
import { Plus, Save, Trash2 } from "lucide-react";
import { updateSystemAdmins } from "../../api/client";
import { useInvalidate, usePortalSettings } from "../../api/queries";
import { CardStatus } from "../../components/cards/CardStatus";
import { CardTitleWithHint } from "../../components/Hint";
import { InlineError } from "../../components/InlineError";

const PORTAL_DOMAIN = "@zenk.co.jp";

/**
 * 全体の管理者(SYSTEM_ADMIN)の一覧(SPEC §2.4)。
 * 以前は環境変数で指定していたため変更にデプロイが要ったが、2026-09-18 に
 * ポータル設定へ移してここから変えられるようにした。
 */
export function AdminSystemAdminsPage() {
  const settings = usePortalSettings();
  const invalidate = useInvalidate();
  const [draft, setDraft] = useState<string[] | null>(null);
  const [added, setAdded] = useState("");

  const current = settings.data?.settings;
  const emails = draft ?? current?.systemAdminEmails ?? [];
  const dirty = draft !== null;

  const save = useMutation({
    mutationFn: () =>
      updateSystemAdmins({ systemAdminEmails: emails, revision: current?.revision ?? 1 }),
    onSuccess: async () => {
      setDraft(null);
      await invalidate.settings();
    },
  });

  const add = () => {
    const email = added.trim().toLowerCase();
    if (email === "" || emails.includes(email)) return;
    setDraft([...emails, email]);
    setAdded("");
  };
  const remove = (email: string) => setDraft(emails.filter((e) => e !== email));

  const invalid = added.trim() !== "" && !added.trim().toLowerCase().endsWith(PORTAL_DOMAIN);
  const lastOne = emails.length <= 1;

  return (
    <Card>
      <CardHeader>
        <CardTitleWithHint hint="候補の承認、ブックマークとお知らせの編集、同期の実行ができる人です。">
          全体の管理者
        </CardTitleWithHint>
        {current?.updatedBy && <CardDescription>最終更新: {current.updatedBy}</CardDescription>}
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <CardStatus
          isPending={settings.isPending}
          isError={settings.isError}
          refetch={() => settings.refetch()}
        />
        {settings.isPending || settings.isError ? null : current == null ? (
          <p className="text-muted-foreground text-sm">
            まだ登録されていません。`pnpm --filter @portal/jobs job seed-admins --emails …`
            で初回の登録を行ってください。
          </p>
        ) : (
          <>
            <ul className="flex flex-col gap-2">
              {emails.map((email) => (
                <li key={email} className="flex items-center gap-2">
                  <span className="grow text-sm">{email}</span>
                  <Button
                    variant="ghost"
                    size="icon"
                    aria-label={`${email} を管理者から外す`}
                    disabled={lastOne}
                    onClick={() => remove(email)}
                  >
                    <Trash2 className="size-4" />
                  </Button>
                </li>
              ))}
            </ul>
            {lastOne ? (
              <p className="text-muted-foreground text-xs">
                管理者は 1 人以上必要です。最後の 1 人は外せません。
              </p>
            ) : null}
            <div className="flex items-start gap-2">
              <div className="grow">
                <Input
                  value={added}
                  onChange={(e) => setAdded(e.target.value)}
                  placeholder={`someone${PORTAL_DOMAIN}`}
                  aria-label="追加する管理者のメールアドレス"
                  onKeyDown={(e) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (!invalid) add();
                    }
                  }}
                />
                {invalid ? (
                  <p className="text-destructive mt-1 text-xs">
                    {PORTAL_DOMAIN} のメールアドレスだけを登録できます。
                  </p>
                ) : null}
              </div>
              <Button variant="outline" onClick={add} disabled={invalid || added.trim() === ""}>
                <Plus className="size-4" />
                追加
              </Button>
            </div>
            <InlineError error={save.error} />
            <div className="flex justify-end">
              <Button onClick={() => save.mutate()} disabled={!dirty || save.isPending}>
                <Save className="size-4" />
                保存
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}
